import type { DisplayVariant } from ".";
import { titleCase } from "../../../../utils/text";
import type { Cover, HelpText, SpecificExcesses } from "../../types";
import type { CoverLimitProps } from "../CoverLimit";

export const formatAmount = (amount: string) => {
  if (!amount) return amount;
  return (
    amount
      .replace(",000,000", "M")
      .replace(/,(\d+)00,000/, (_, digits) => `.${digits}M`)
      .replace(/,(\d+)0,000/, (_, digits) => `.${digits}M`)
      .replace(",000", "K")
      // Added below for ProductSummary card
      .replace(/([£$]\d+)k/g, "$1K")
      .replace(/([£$]\d+)m/g, "$1M")
  );
};

export const findHelpText = (
  helpTextArray: HelpText[],
  coverage: string,
): string | undefined => {
  const helpTextObject = helpTextArray.find(
    item => item.coverage.toLowerCase() === coverage.toLowerCase(),
  );
  return helpTextObject && helpTextObject.text;
};

// {property Damage Deductible Per Claim: '$0'}
export const formatCoverLimit = (
  cover: string,
  helpText: HelpText[] = [],
): CoverLimitProps => {
  const [title, amount] = cover.split(": ");
  const explainer = findHelpText(helpText, title);
  // Adjust title for Claim Limit
  let displayTitle = titleCase(title);
  if (displayTitle === "Claims Made Limit") {
    displayTitle = "Claim Limit";
  }

  return {
    title: displayTitle,
    amount: formatAmount(amount),
    explainer,
  };
};

export const extractCoverAmount = (cover: string): string => {
  const [, amount] = cover.split(": ");
  return amount;
};

export const formatExcesses = (
  displayExcess: string,
  specificExcesses: SpecificExcesses[] = [],
  helpText: HelpText[] = [],
): CoverLimitProps[] => {
  const allExcesses = [
    ...specificExcesses,
    { excessName: "Deductibles", excessValue: displayExcess },
  ];

  return allExcesses.map(({ excessName, excessValue }) => {
    return {
      title: titleCase(excessName),
      amount: formatAmount(excessValue),
      explainer: findHelpText(helpText, excessName),
    };
  });
};

export const transformCoverage = (covers: Cover[]) => {
  return covers.filter(
    c =>
      c.quoteDetailsDisplayLimits.length > 0 ||
      c.coverName?.match(/^Other States Coverage?$/i),
  );
};

export const transformExtraCoverage = (covers: Cover[]) => {
  return covers.filter(c => c.extraCover);
};

export const isAdditionalInsured = (cover: Cover) => {
  return cover.coverName.match(/^Additional insureds?$/i);
};

export const isLimitIncluded = (limit: string) => {
  const lowerLimit = limit.toLowerCase();
  return (
    lowerLimit.includes("occurrence limit") ||
    lowerLimit.includes("aggregate limit")
  );
};

export const getCoverLimits = (
  product: string,
  limits: string[],
  filterQcpLimits: boolean,
  displayVariant: DisplayVariant,
  helpText: HelpText[],
  withModal?: boolean,
): CoverLimitProps[] => {
  const filteredLimits =
    filterQcpLimits && displayVariant === "qcp"
      ? limits.filter(isLimitIncluded)
      : limits;

  return filteredLimits.map(limit => {
    const { title, amount, explainer } = formatCoverLimit(limit, helpText);
    return {
      product,
      title,
      amount,
      explainer,
      withModal,
    };
  });
};
